// Server-buildable preview texts. Lives in shared/ so the same logic runs
// on the client (rich rendering, ESM imports) and the server (security:
// when /api/tts/synthesize gets a `preview` request, the server builds the
// text from the request's `kind` rather than trusting the client to pass
// arbitrary text — preview mode bypasses tier gating so we can't trust the
// text field).

import * as languageMap from "./languageMap.js";

/**
 * Neutral per-language preview greeting. Used for the play-circle button on
 * each voice row, where saying "you selected this voice" would be wrong
 * (the user is only previewing, not selecting).
 *
 * @type {{[language: string]: string}}
 */
export const neutralSamples = {
  en: "Hello! This is a short sample so you can hear this voice.",
  es: "Hola. Esta es una breve muestra para que puedas escuchar esta voz.",
  fr: "Bonjour. Voici un court échantillon pour que vous entendiez cette voix.",
  de: "Hallo. Dies ist ein kurzes Beispiel, damit Sie diese Stimme hören können.",
  it: "Ciao. Questo è un breve campione per farti ascoltare questa voce.",
  pt: "Olá. Esta é uma breve amostra para que você ouça esta voz.",
  nl: "Hallo. Dit is een korte fragment zodat je deze stem kunt horen.",
  sv: "Hej. Det här är ett kort ljudprov så att du kan höra den här rösten.",
  no: "Hei. Dette er en kort lydprøve så du kan høre denne stemmen.",
  da: "Hej. Dette er et kort lydprøve, så du kan høre denne stemme.",
  fi: "Hei. Tämä on lyhyt näyte, jotta voit kuulla tämän äänen.",
  pl: "Cześć. To krótka próbka, aby usłyszeć ten głos.",
  ru: "Привет. Это короткий образец, чтобы вы услышали этот голос.",
  tr: "Merhaba. Bu sesi duyabilmeniz için kısa bir örnek.",
  ja: "こんにちは。この声を聞くための短いサンプルです。",
  ko: "안녕하세요. 이 목소리를 들을 수 있는 짧은 샘플입니다.",
  zh: "你好。这是一段简短的试听样本。",
  "zh-TW": "你好。這是一段簡短的試聽樣本。",
  ar: "مرحبا. هذه عينة قصيرة لتسمع هذا الصوت.",
  el: "Γεια σας. Αυτό είναι ένα σύντομο δείγμα για να ακούσετε αυτή τη φωνή.",
  uk: "Привіт. Це короткий зразок, щоб ви почули цей голос.",
  cs: "Ahoj. Toto je krátký vzorek, abyste si poslechli tento hlas.",
  hu: "Szia. Ez egy rövid minta, hogy hallhasd ezt a hangot.",
  ro: "Salut. Acesta este un scurt eșantion pentru a auzi această voce.",
  vi: "Xin chào. Đây là một mẫu ngắn để bạn nghe giọng nói này.",
  hi: "नमस्ते। यह इस आवाज़ को सुनने के लिए एक छोटा सा नमूना है।",
  id: "Halo. Ini adalah sampel singkat agar Anda dapat mendengar suara ini.",
  th: "สวัสดี นี่คือตัวอย่างสั้นๆ เพื่อให้คุณได้ยินเสียงนี้",
  // Latin script: most Serbian texts on Readlang are Latin, so this previews
  // the voice a listener will actually hear (the server picks the matching
  // script variant per request — see voiceForTextScript in src/msSpeech.js).
  sr: "Zdravo. Ovo je kratak uzorak da čujete ovaj glas.",
};

/**
 * Returns a localized "voice X has been selected" announcement, used as the
 * confirmation when a user picks a voice. Falls back to just the voice name
 * if no template exists for the language.
 *
 * @param {string} language
 * @param {string} voiceName
 * @returns {string}
 */
export const voiceSelectedText = (language, voiceName) => {
  const template = languageMap.map[language]?.ttsVoiceSelectedText;
  if (template) return template.replace("{{voiceName}}", voiceName);
  return voiceName;
};

/**
 * Builds the preview text for a given `kind`. Used both client-side (when
 * the caller is authorized and just needs the right localized string) and
 * server-side (where we MUST control the exact text because preview mode
 * bypasses tier gating).
 *
 * @param {"neutralSample" | "voiceSelected"} kind
 * @param {string} language
 * @param {string} voiceName Display name of the voice being previewed.
 *   Required for `voiceSelected` and used as fallback for `neutralSample`.
 * @returns {string}
 */
export const buildPreviewText = (kind, language, voiceName) => {
  if (kind === "voiceSelected") {
    return voiceSelectedText(language, voiceName);
  }
  // neutralSample (and any unknown kinds): prefer the neutral greeting;
  // fall back to the localized voice-selected announcement so we still
  // produce a meaningful, localized sample rather than just the language
  // name in English.
  if (neutralSamples[language]) return neutralSamples[language];
  return voiceSelectedText(language, voiceName);
};
